import { navigate } from "../router.js";
import type { ReactNode } from "react";
import { GameIcon } from "../assets/gameIcons.js";
import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect, useRef, useState } from "react";
import { useSceneStore } from "../scene/SceneHost.js";
import { onWs } from "../api/ws.js";
import { useT } from "../i18n/index.js";
import { toast } from "../ui/Toast.js";
import { Timer, useServerTick } from "../ui/Timer.js";
import { useSession } from "../state/session.js";
import { WorldScene } from "./WorldScene.js";
import { StaminaStock } from "./StaminaStock.js";
import { HuntChip } from "./HuntChip.js";
import { MarchDetail } from "./MarchDetail.js";
import type { MarchModelState } from "./MarchColumn.js";
import { WorldLabels } from "./WorldLabels.js";
import { Minimap } from "./Minimap.js";
import { TileSheet } from "./TileSheet.js";
import { AttacksPanel, BookmarksPanel, BossPanel, FindPanel, MarchList, ScoutsPanel } from "./panels.js";
import { ALL_KINDS, marchOutcomeText, useWorld } from "./state.js";
import { kindName } from "./TileSheet.js";
import type { MarchView } from "./types.js";
import type { BossModelState } from "./WorldBoss.js";
import { activeIncoming, WORLD_ZOOMS, type WorldZoom } from "./presentation.js";
import "./world-presentation.css";
import { useReducedMotion } from "../units/useReducedMotion.js";
import { useUnits } from "../units/state.js";

/**
 * The world map screen (50-world.md): attaches the WorldScene to the shared renderer, keeps the view loaded
 * around the camera, and renders chips, side tools, labels, minimap, tile sheet and the panels.
 */
export function WorldView({ active }: { active: boolean }) {
  const t = useT();
  const handle = useSceneStore((s) => s.handle);
  const [scene, setScene] = useState<WorldScene | null>(null);
  const sceneRef = useRef<WorldScene | null>(null);
  const me = useWorld((s) => s.me);
  const view = useWorld((s) => s.view);
  const selection = useWorld((s) => s.selection);
  const panel = useWorld((s) => s.panel);
  const selectedMarch = useWorld((s) => s.selectedMarch);
  const [marchStates, setMarchStates] = useState<Record<string, MarchModelState>>({});
  const jump = useWorld((s) => s.jump);
  const hidden = useWorld((s) => s.hidden);
  const setPanel = useWorld((s) => s.setPanel);
  const refreshMe = useWorld((s) => s.refreshMe);
  const account = useSession((s) => s.account);
  const [centre, setCentre] = useState({ x: 150, y: 150 });
  const [filters, setFilters] = useState(false);
  const [zoom, setZoom] = useState<WorldZoom>("tactical");
  const [bossState, setBossState] = useState<BossModelState>("hidden");
  const reducedMotion = useReducedMotion();
  const now = useServerTick(1);
  const refreshedStaminaDeadline = useRef<number | null>(null);
  useEffect(() => {
    const deadline = me?.stamina.nextAt;
    if (active && deadline != null && now >= deadline && refreshedStaminaDeadline.current !== deadline) {
      refreshedStaminaDeadline.current = deadline; void refreshMe();
    }
  }, [active, me?.stamina.nextAt, now, refreshMe]);
  useEffect(() => {
    const ids = new Set(me?.marches.map((march) => march.id));
    setMarchStates((previous) => Object.fromEntries(Object.entries(previous).filter(([id]) => ids.has(id))));
  }, [me?.marches]);

  // Scene lifecycle: created when the map is active and the renderer exists.
  useEffect(() => {
    if (!handle || !active) return;
    let cancelled = false;
    const s = new WorldScene(handle, {
      onTap: (x, y) => {
        const st = useWorld.getState();
        const tile = st.view?.tiles.find((tt) => tt.x === x && tt.y === y) ?? null;
        if (st.selection && st.selection.x === x && st.selection.y === y) st.select(null);
        else st.select({ x, y, tile });
      },
      onMove: (x, y) => {
        setCentre({ x, y });
        void useWorld.getState().loadAround(x, y);
      },
      onZoom: setZoom,
      onBossState: setBossState,
      onMarchState: (id, state) => setMarchStates((previous) => ({ ...previous, [id]: state })),
      onMarchTap: (id) => useWorld.getState().selectMarch(id),
    });
    sceneRef.current = s;
    setScene(s);
    void (async () => {
      await refreshMe();
      if (cancelled) return;
      const pos = useWorld.getState().me?.position ?? { x: 150, y: 150 };
      s.takeCamera(pos);
      // A jump requested before the map mounted (chat coordinates, profile) wins over the home position.
      const pending = useWorld.getState().jump;
      if (pending) {
        s.jumpTo(pending.x, pending.y);
        useWorld.getState().setJump(null);
      }
    })();
    return () => {
      cancelled = true;
      s.dispose();
      sceneRef.current = null;
      setScene(null);
    };
  }, [handle, active, refreshMe]);

  // Pushes and polling.
  useEffect(() => {
    if (!active) return;
    const offMarch = onWs("march.update", (e) => {
      const m = e.payload as MarchView;
      const st = useWorld.getState();
      st.applyMarch(m);
      if (m.result?.battleId && st.watchedMarch === m.id) { useWorld.setState({ watchedMarch: null }); navigate("/app/battle/"+m.result.battleId); }
      if (m.state === "done" || m.state === "returning") {
        const text = marchOutcomeText(m);
        if (text && m.result?.outcome !== "gathering") toast.info(`${t(`world.action.${m.action}`)}: ${text}`);
        if (m.state === "done") { void st.refreshMe(); void useUnits.getState().refreshRoster(); }
      }
      if (m.state === "gathering" || m.state === "returning") void st.loadAround(useWorld.getState().view?.centre.x ?? m.to.x, useWorld.getState().view?.centre.y ?? m.to.y);
    });
    const offIncoming = onWs("attack.incoming", (e) => {
      const p = e.payload as { attackerName: string; hidden?: boolean };
      if (!p.hidden) toast.error(t("world.toast.incoming", { name: p.attackerName }));
      void useWorld.getState().refreshMe();
    });
    const offReport = onWs("attack.report", (e) => {
      const p = e.payload as { won: boolean; role: string; attackerName: string; defenderName: string };
      toast[p.won ? "success" : "error"](p.role === "defender" ? t(p.won ? "world.toast.defended" : "world.toast.raided", { name: p.attackerName }) : t(p.won ? "world.toast.raidWon" : "world.toast.raidLost", { name: p.defenderName }));
      void useWorld.getState().refreshMe();
    });
    const offShield = onWs("shield.changed", () => void useWorld.getState().refreshMe());
    const offBoss = onWs("boss.spawned", (e) => {
      const p = e.payload as { x: number; y: number };
      toast.info(t("world.toast.boss", { x: p.x, y: p.y }));
    });
    const offScout = onWs("scout.report", () => toast.info(t("world.toast.scoutReport")));
    const poll = setInterval(() => void useWorld.getState().refreshMe(), 30_000);
    return () => {
      offMarch();
      offIncoming();
      offReport();
      offShield();
      offBoss();
      offScout();
      clearInterval(poll);
    };
  }, [active, t]);

  useEffect(() => {
    if (view && scene) scene.setView(view, me);
  }, [view, scene]);
  useEffect(() => scene?.setMe(me), [me, scene]);
  useEffect(() => scene?.setReducedMotion(reducedMotion), [reducedMotion, scene]);
  useEffect(() => scene?.setSelection(selection ? { x: selection.x, y: selection.y } : null), [selection, scene]);
  useEffect(() => scene?.setHidden(hidden), [hidden, scene]);
  useEffect(() => scene?.selectMarch(selectedMarch), [scene, selectedMarch]);
  useEffect(() => {
    if (!jump || !scene) return;
    scene.jumpTo(jump.x, jump.y);
    useWorld.getState().setJump(null);
  }, [jump, scene]);

  if (!active) return null;
  const shieldUntil = me ? Math.max(me.shieldUntil ?? 0, me.newbieUntil ?? 0) : 0;
  const incoming = activeIncoming(me?.incoming ?? [], now);
  const nextHit = incoming.length ? Math.min(...incoming.map((i) => i.arrivesAt)) : null;
  const activeMarches = me?.marches.filter((m) => m.state !== "done" && m.action !== "scout") ?? [];
  const tools: { key: string; icon: ReactNode; panel: "marches" | "find" | "bookmarks" | "scouts" | "boss" | "attacks"; testId: string }[] = [
    { key: "world.marches", icon: <FunctionalIcon name="send" />, panel: "marches", testId: "tool-marches" },
    { key: "world.find", icon: <FunctionalIcon name="search" />, panel: "find", testId: "tool-find" },
    { key: "world.bookmarks", icon: <FunctionalIcon name="bookmark" />, panel: "bookmarks", testId: "tool-bookmarks" },
    { key: "world.scouts", icon: <FunctionalIcon name="eye" />, panel: "scouts", testId: "tool-scouts" },
    { key: "world.boss", icon: <GameIcon id="zmb.boss_titan_necrolith" size={28} />, panel: "boss", testId: "tool-boss" },
    { key: "world.attacks", icon: <FunctionalIcon name="attack" />, panel: "attacks", testId: "tool-attacks" },
  ];

  return (
    <div className="world-view" data-testid="map-view" data-zoom={zoom} data-reduced-motion={reducedMotion} style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      <WorldLabels scene={scene} bossState={bossState} now={now} />
      <div className="chips" data-testid="map-chips">
        <button type="button" className="chip mono" onClick={() => me && useWorld.getState().setJump(me.position)} data-testid="chip-home">
          <FunctionalIcon name="home" /> {me ? `${me.position.x},${me.position.y}` : "…"}
        </button>
        <span className="chip mono" data-testid="chip-centre"><FunctionalIcon name="location" /> {centre.x},{centre.y}</span>
        <StaminaStock stamina={me?.stamina} />
        <HuntChip />
        <button type="button" className="chip" onClick={() => setPanel("marches")} data-testid="chip-marches">
          <FunctionalIcon name="send" /> {activeMarches.length}/{me?.marchSlots ?? 1}
        </button>
        {me ? (
          shieldUntil > now ? (
            <span className="chip ok" data-testid="chip-shield">
              <GameIcon id="emote.shield" /> <Timer finishesAt={shieldUntil} />
            </span>
          ) : (
            <button type="button" className="chip warn" onClick={() => me && useWorld.getState().select({ x: me.position.x, y: me.position.y, tile: view?.tiles.find((x) => x.owner?.id === account?.id) ?? null })} data-testid="chip-shield">
              <GameIcon id="emote.shield" /> {t("world.noShield")}
            </button>
          )
        ) : null}
        {me?.burningUntil && me.burningUntil > now ? (
          <span className="chip warn" data-testid="chip-burning">
            <GameIcon id="emote.fire" /> <Timer finishesAt={me.burningUntil} />
          </span>
        ) : null}
        {nextHit ? (
          <button type="button" className="chip warn alarm" onClick={() => setPanel("attacks")} data-testid="chip-incoming">
            <FunctionalIcon name="warning" /> {t("world.incoming", { n: incoming.length })} <Timer finishesAt={nextHit} />
          </button>
        ) : null}
      </div>
      <div className="world-zoom glass" role="group" aria-label={t("world.zoom")} data-testid="world-zoom">
        {WORLD_ZOOMS.map((preset) => <button key={preset.id} type="button" aria-pressed={zoom === preset.id} onClick={() => scene?.setZoom(preset.id)} data-testid={`world-zoom-${preset.id}`}>
          {t(`world.zoom.${preset.id}`)}
        </button>)}
      </div>
      <div className="map-tools" data-testid="map-tools">
        {tools.map((tool) => (
          <button key={tool.panel} type="button" className={`map-tool glass ${panel === tool.panel ? "active" : ""}`} title={t(tool.key)} onClick={() => setPanel(panel === tool.panel ? "none" : tool.panel)} data-testid={tool.testId}>
            {tool.icon}
          </button>
        ))}
        <button type="button" className={`map-tool glass ${filters ? "active" : ""}`} title={t("world.filters")} onClick={() => setFilters(!filters)} data-testid="tool-filters">
          <FunctionalIcon name="filter" />
        </button>
      </div>
      {filters ? (
        <div className="glass map-filters" data-testid="map-filters">
          {ALL_KINDS.map((k) => (
            <label key={k} className="row" style={{ gap: 6, fontSize: 12 }}>
              <input type="checkbox" checked={!hidden.has(k)} onChange={() => useWorld.getState().toggleKind(k)} />
              {kindName(k, t)}
            </label>
          ))}
        </div>
      ) : null}
      <div className="minimap-wrap">
        <Minimap centre={centre} bounds={scene?.groundBounds() ?? null} />
      </div>
      {panel === "marches" ? <MarchList /> : null}
      {panel === "find" ? <FindPanel /> : null}
      {panel === "bookmarks" ? <BookmarksPanel /> : null}
      {panel === "scouts" ? <ScoutsPanel /> : null}
      {panel === "boss" ? <BossPanel /> : null}
      {panel === "attacks" ? <AttacksPanel /> : null}
      {panel === "none" && !selectedMarch ? <TileSheet /> : null}
      {selectedMarch ? <MarchDetail scene={scene} modelState={marchStates[selectedMarch]} /> : null}
      {view === null ? (
        <div className="dim" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)" }} data-testid="map-loading">
          {t("common.loading")}
        </div>
      ) : null}
    </div>
  );
}
